import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { ALPHA_ATLAS_SIGNAL_DURATIONS, type AlphaAtlasEntity } from '@einhaus/project314-types';
import type { AppRouter } from '@/types/appRouter';

type RouterInputs = inferRouterInputs<AppRouter>;
type RouterOutputs = inferRouterOutputs<AppRouter>;

export type AlphaAtlasReportInput = RouterInputs['alphaAtlas']['reportResults'];
export type AlphaAtlasReportPage = RouterOutputs['alphaAtlas']['reportResults'];
export type AlphaAtlasReportRow = AlphaAtlasReportPage['items'][number];
export type AlphaAtlasReportSource = AlphaAtlasReportPage['source'];
export type AlphaAtlasReportFilters = RouterOutputs['alphaAtlas']['reportFilters'];
export type AlphaAtlasExposure = RouterOutputs['alphaAtlas']['revealFullHistory'];
export type AlphaAtlasCombinationInput = RouterInputs['alphaAtlas']['combinationDetail'];
export type AlphaAtlasCombinationDetail = RouterOutputs['alphaAtlas']['combinationDetail'];
export type AlphaAtlasSliceAvailability = RouterOutputs['alphaAtlas']['availableSlices'][number];

export type AlphaAtlasViewState = 'loading' | 'error' | 'empty' | 'ready';
export type AlphaAtlasDuration = AlphaAtlasReportSource['durationId'];
export type AlphaAtlasOutcomeKind = AlphaAtlasReportSource['outcomeKind'];
export type AlphaAtlasScope = 'entity' | 'pooled';
export type AlphaAtlasConditionKind = 'parentOnly' | 'childOnly' | 'parentAndChild';
export type AlphaAtlasDirection = 'long' | 'short';
export type AlphaAtlasWindowRole = 'training' | 'selectionValidation' | 'verification' | 'wholeRange' | 'recentYear';
export type AlphaAtlasEvidenceState =
    | 'unavailable'
    | 'insufficientSupport'
    | 'notSelected'
    | 'failedVerification'
    | 'validatedHistorical'
    | 'promisingSparse'
    | 'prospectiveConfirmed';
export type AlphaAtlasSortBy = NonNullable<AlphaAtlasReportInput['sortBy']>;
export type AlphaAtlasAcrossWindows = NonNullable<AlphaAtlasReportInput['acrossWindows']>;
/** Page modes: rank everything ("find edge") or inspect one combination across horizons and windows ("lookup"). */
export type AlphaAtlasView = 'find' | 'lookup';

export const alphaAtlasEntityKey = (entity: AlphaAtlasEntity): string => `${entity.entityType}:${entity.entityId}`;
export const parseAlphaAtlasEntityKey = (key: string): AlphaAtlasEntity => {
    const [entityType, id, extra] = key.split(':');
    const entityId = Number(id);
    if (extra !== undefined || (entityType !== 'Asset' && entityType !== 'AssetIndex') || !Number.isSafeInteger(entityId) || entityId <= 0)
        throw new Error(`Invalid Alpha Atlas entity key: ${key}`);
    return { entityType, entityId };
};
export const alphaAtlasRowEntityKeys = (row: { entityType: AlphaAtlasEntity['entityType'] | null; entityId: number | null }): string[] => {
    if (row.entityId === null || row.entityType === null) return [];
    return [alphaAtlasEntityKey({ entityType: row.entityType, entityId: row.entityId })];
};

export interface AlphaAtlasFilterState {
    view: AlphaAtlasView;
    durationId: AlphaAtlasDuration;
    outcomeKind: AlphaAtlasOutcomeKind;
    scope: AlphaAtlasScope;
    /** Entity-scope rows are limited to these entities; empty means every entity (when scope is `entity`) */
    entityKeys: string[];
    conditionKind?: AlphaAtlasConditionKind | undefined;
    hypothesisFingerprint?: string | undefined;
    parentFingerprint?: string | undefined;
    childFingerprint?: string | undefined;
    direction?: AlphaAtlasDirection | undefined;
    horizonSeconds?: number | undefined;
    windowRole: AlphaAtlasWindowRole;
    evidenceState?: AlphaAtlasEvidenceState | undefined;
    exposureFingerprint?: string | undefined;
    hideInsufficientSupport: boolean;
    /** User-chosen floor on measurable observations, on top of the evidence rules */
    minSupport?: number | undefined;
    /** Requirement on the selection-validation (walk-forward) twin of each row */
    acrossWindows: AlphaAtlasAcrossWindows;
    /** Aggregate the selected entities into one result per hypothesis instead of one row per entity */
    bundleEntities: boolean;
    sortBy: AlphaAtlasSortBy;
    page: number;
    pageSize: number;
}

export const ALPHA_ATLAS_PAGE_SIZE = 50;

export const DEFAULT_ALPHA_ATLAS_FILTERS: AlphaAtlasFilterState = {
    view: 'find',
    durationId: '1d',
    outcomeKind: 'underlying',
    scope: 'pooled',
    entityKeys: [],
    windowRole: 'training',
    hideInsufficientSupport: true,
    acrossWindows: 'any',
    bundleEntities: false,
    sortBy: 'randomEdge',
    page: 1,
    pageSize: ALPHA_ATLAS_PAGE_SIZE
};

export const ACROSS_WINDOWS_META: Record<AlphaAtlasAcrossWindows, { label: string; description: string }> = {
    any: { label: 'Any', description: 'No requirement on the walk-forward window.' },
    supported: {
        label: 'Enough data in both windows',
        description: 'The same hypothesis must also clear the evidence rules (and your min instances) in the selection-validation window.'
    },
    holds: {
        label: 'Holds up in both windows',
        description:
            'Filters to enough data and a positive edge in both controlled windows. It does not change ranking; use Weaker-window edge to rank by robustness.'
    }
};
export const ACROSS_WINDOWS_ORDER: AlphaAtlasAcrossWindows[] = ['any', 'supported', 'holds'];

const ALPHA_ATLAS_DURATION_LABELS: Record<AlphaAtlasDuration, string> = {
    '1d': 'Daily',
    '240m': '240-minute',
    '30m': '30-minute'
};

export const ALPHA_ATLAS_DURATIONS: { value: AlphaAtlasDuration; label: string }[] = ALPHA_ATLAS_SIGNAL_DURATIONS.map((value) => ({
    value,
    label: ALPHA_ATLAS_DURATION_LABELS[value]
}));

export const ALPHA_ATLAS_OUTCOMES: { value: AlphaAtlasOutcomeKind; label: string }[] = [
    { value: 'underlying', label: 'Underlying' },
    { value: 'option', label: 'Options' }
];

export const alphaAtlasDirectionLabel = (outcomeKind: AlphaAtlasOutcomeKind, direction: AlphaAtlasDirection): string => {
    if (outcomeKind === 'option') return direction === 'long' ? 'Call' : 'Put';
    return direction === 'long' ? 'Long' : 'Short';
};

export const isAlphaAtlasSliceAvailable = (
    slices: readonly AlphaAtlasSliceAvailability[] | undefined,
    durationId: AlphaAtlasDuration,
    outcomeKind: AlphaAtlasOutcomeKind
): boolean => slices?.some((slice) => slice.durationId === durationId && slice.outcomeKind === outcomeKind && slice.available) ?? false;

/** An outcome change keeps signal-catalog filters, but requester-bound and publication-specific state cannot cross slices. */
export const changeAlphaAtlasOutcome = (filters: AlphaAtlasFilterState, outcomeKind: AlphaAtlasOutcomeKind): AlphaAtlasFilterState => ({
    ...filters,
    outcomeKind,
    hypothesisFingerprint: undefined,
    exposureFingerprint: undefined,
    windowRole: isVerificationBearingWindow(filters.windowRole) ? 'training' : filters.windowRole,
    page: 1
});

/** Drops signal filters that do not exist in the newly loaded slice while preserving every compatible coordinate. */
export const reconcileAlphaAtlasFiltersForSlice = (
    filters: AlphaAtlasFilterState,
    options: AlphaAtlasReportFilters
): AlphaAtlasFilterState => {
    const entityUniverse = new Set(options.entities.map(alphaAtlasEntityKey));
    const entityKeys = filters.entityKeys.filter((entityId) => entityUniverse.has(entityId));
    const scope = filters.scope === 'entity' && entityKeys.length > 0 ? 'entity' : 'pooled';
    const parentFingerprints = new Set(options.parents.map((parent) => parent.fingerprint));
    const childFingerprints = new Set(options.children.map((child) => child.fingerprint));
    const horizons = new Set(options.horizons.map((horizon) => horizon.horizonSeconds));
    return {
        ...filters,
        scope,
        entityKeys,
        parentFingerprint:
            filters.parentFingerprint && parentFingerprints.has(filters.parentFingerprint) ? filters.parentFingerprint : undefined,
        childFingerprint:
            filters.childFingerprint && childFingerprints.has(filters.childFingerprint) ? filters.childFingerprint : undefined,
        horizonSeconds: filters.horizonSeconds && horizons.has(filters.horizonSeconds) ? filters.horizonSeconds : undefined,
        bundleEntities: filters.bundleEntities && scope === 'entity' && entityKeys.length >= 2
    };
};

export const ALPHA_ATLAS_WINDOW_ROLES: AlphaAtlasWindowRole[] = [
    'training',
    'selectionValidation',
    'verification',
    'wholeRange',
    'recentYear'
];

export const WINDOW_ROLE_META: Record<
    AlphaAtlasWindowRole,
    { label: string; shortLabel: string; description: string; controlled: boolean }
> = {
    training: {
        label: 'Historical (controlled)',
        shortLabel: 'Historical',
        description:
            'Canonical start through one year before the publication "as of" time. Reading it never consumes verification evidence.',
        controlled: true
    },
    selectionValidation: {
        label: 'Selection validation (controlled)',
        shortLabel: 'Selection validation',
        description: 'One year to six months before "as of". The out-of-sample check used to select candidates; still safe to read.',
        controlled: true
    },
    verification: {
        label: 'Verification (revealed)',
        shortLabel: 'Verification',
        description:
            'Six months before "as of" through the latest mature decision. Reading it burns that interval as independent evidence.',
        controlled: false
    },
    wholeRange: {
        label: 'Whole range (exploratory)',
        shortLabel: 'Whole range',
        description: 'Canonical start through the latest mature decision. Includes the verification interval, so it is exploratory only.',
        controlled: false
    },
    recentYear: {
        label: 'Recent year (exploratory)',
        shortLabel: 'Recent year',
        description:
            'The most recent year through the latest mature decision. Includes the verification interval, so it is exploratory only.',
        controlled: false
    }
};

export const EVIDENCE_STATE_META: Record<
    AlphaAtlasEvidenceState,
    { label: string; description: string; tone: 'neutral' | 'info' | 'success' | 'danger' | 'warning' | 'accent' }
> = {
    unavailable: { label: 'Unavailable', description: 'The required source or window is absent or empty.', tone: 'neutral' },
    insufficientSupport: {
        label: 'Insufficient support',
        description: 'Fails the minimum support, breadth, or independent-block rules. Numbers are shown but should not be trusted.',
        tone: 'neutral'
    },
    notSelected: {
        label: 'Not selected',
        description: 'Enough support to be eligible, but did not survive the frozen ranking and multiplicity rules of the last campaign.',
        tone: 'info'
    },
    failedVerification: {
        label: 'Failed verification',
        description: 'Selected as a candidate but failed the verification policy.',
        tone: 'danger'
    },
    validatedHistorical: {
        label: 'Historically validated',
        description: 'Passed controlled historical verification. Still not prospective proof and never promotable from here.',
        tone: 'success'
    },
    promisingSparse: {
        label: 'Promising, sparse',
        description: 'Large, consistent, non-concentrated effect with inadequate support. Watchlist only; never promotable.',
        tone: 'warning'
    },
    prospectiveConfirmed: {
        label: 'Prospectively confirmed',
        description: 'Separately governed paper or live-forward evidence passed.',
        tone: 'success'
    }
};

export const EVIDENCE_STATE_FILTER_ORDER: AlphaAtlasEvidenceState[] = [
    'validatedHistorical',
    'promisingSparse',
    'notSelected',
    'failedVerification',
    'insufficientSupport',
    'unavailable'
];

export const CONDITION_KIND_META: Record<AlphaAtlasConditionKind, { label: string; description: string }> = {
    parentAndChild: { label: 'Trigger + context', description: 'A trigger event that fired while a context state was present.' },
    parentOnly: { label: 'Trigger only', description: 'A trigger event on its own; the baseline a context should improve on.' },
    childOnly: { label: 'Context only', description: 'A context state on its own, regardless of trigger.' }
};

export const SORT_META: Record<AlphaAtlasSortBy, { label: string; description: string }> = {
    randomEdge: { label: 'Edge vs random', description: 'Net mean return minus the exposure-matched random entry, highest first.' },
    netReturn: { label: 'Net return', description: 'Cost-adjusted mean return, highest first.' },
    ciLower: {
        label: 'CI lower bound',
        description: 'Lower bound of the 95% interval of the net return, highest first; favors consistent results.'
    },
    support: { label: 'Support', description: 'Observation count, highest first.' },
    lift: {
        label: 'Lift vs trigger',
        description:
            'Net return of the trigger + context combination minus the trigger alone at the same coordinates, highest first: what the context adds.'
    },
    liftVsContext: {
        label: 'Lift vs context',
        description:
            'Net return of the trigger + context combination minus the context alone at the same coordinates, highest first: what the trigger adds.'
    },
    validationRandomEdge: {
        label: 'Validation edge vs random',
        description: 'Edge vs random entry for the same hypothesis in the selection-validation (walk-forward) window, highest first.'
    },
    validationLift: {
        label: 'Validation lift',
        description: 'Lift vs trigger alone for the same hypothesis in the selection-validation (walk-forward) window, highest first.'
    },
    validationLiftVsContext: {
        label: 'Validation lift vs context',
        description: 'Lift vs context alone in the selection-validation (walk-forward) window, highest first.'
    },
    worstWindowEdge: {
        label: 'Weaker-window edge',
        description:
            'The lower edge vs random entry across the historical and selection-validation windows, highest first. Requires results in both windows.'
    }
};

export const ALPHA_ATLAS_VIEWS: { value: AlphaAtlasView; label: string }[] = [
    { value: 'find', label: 'Find edge' },
    { value: 'lookup', label: 'Look up a hypothesis' }
];

export const isVerificationBearingWindow = (windowRole: AlphaAtlasWindowRole): boolean => !WINDOW_ROLE_META[windowRole].controlled;

/** Bundling needs at least two selected entities and a controlled window (revealed windows are per hypothesis, never per bundle). */
export const isBundleActive = (filters: Pick<AlphaAtlasFilterState, 'scope' | 'entityKeys' | 'bundleEntities' | 'windowRole'>): boolean =>
    filters.bundleEntities &&
    filters.scope === 'entity' &&
    filters.entityKeys.length >= 2 &&
    !isVerificationBearingWindow(filters.windowRole);

export const buildAlphaAtlasQuery = (filters: AlphaAtlasFilterState): AlphaAtlasReportInput => ({
    durationId: filters.durationId,
    outcomeKind: filters.outcomeKind,
    scope: filters.scope,
    entities: filters.scope === 'entity' && filters.entityKeys.length > 0 ? filters.entityKeys.map(parseAlphaAtlasEntityKey) : undefined,
    conditionKind: filters.conditionKind,
    hypothesisFingerprint: filters.hypothesisFingerprint,
    parentFingerprint: filters.parentFingerprint,
    childFingerprint: filters.childFingerprint,
    direction: filters.direction,
    horizonSeconds: filters.horizonSeconds,
    windowRole: filters.windowRole,
    evidenceState: filters.evidenceState,
    exposureFingerprint: isVerificationBearingWindow(filters.windowRole) ? filters.exposureFingerprint : undefined,
    hideInsufficientSupport: filters.hideInsufficientSupport,
    minSupport: filters.minSupport,
    acrossWindows: filters.acrossWindows,
    bundleEntities: isBundleActive(filters) ? true : undefined,
    sortBy: filters.sortBy,
    page: filters.page,
    pageSize: filters.pageSize
});

export const resolveAlphaAtlasViewState = (loading: boolean, error: { message: string } | null, rowCount: number): AlphaAtlasViewState => {
    if (loading) return 'loading';
    if (error) return 'error';
    return rowCount === 0 ? 'empty' : 'ready';
};

/* ---------- URL state ---------- */

const FINGERPRINT = /^[0-9a-f]{64}$/i;
const VIEWS = new Set<string>(['find', 'lookup']);
const DURATIONS = new Set<string>(ALPHA_ATLAS_SIGNAL_DURATIONS);
const OUTCOMES = new Set<string>(['underlying', 'option']);
const SCOPES = new Set<string>(['entity', 'pooled']);
const CONDITION_KINDS = new Set<string>(['parentOnly', 'childOnly', 'parentAndChild']);
const DIRECTIONS = new Set<string>(['long', 'short']);
const WINDOW_ROLES = new Set<string>(ALPHA_ATLAS_WINDOW_ROLES);
const EVIDENCE_STATES = new Set<string>(Object.keys(EVIDENCE_STATE_META));
const SORTS = new Set<string>(Object.keys(SORT_META));
const ACROSS = new Set<string>(['any', 'supported', 'holds']);

const enumParam = <T extends string>(params: URLSearchParams, key: string, allowed: Set<string>): T | undefined => {
    const value = params.get(key);
    return value !== null && allowed.has(value) ? (value as T) : undefined;
};
const positiveIntParam = (params: URLSearchParams, key: string): number | undefined => {
    const value = Number(params.get(key));
    return Number.isInteger(value) && value > 0 ? value : undefined;
};
/** Comma-separated complete identities; malformed URL entries are ignored. */
const entityKeyListParam = (params: URLSearchParams, key: string): string[] => {
    const valid = new Set<string>();
    for (const part of (params.get(key) ?? '').split(',')) {
        if (!/^(Asset|AssetIndex):[1-9]\d*$/.test(part)) continue;
        const id = Number(part.split(':')[1]);
        if (Number.isSafeInteger(id)) valid.add(part);
    }
    return [...valid];
};
const fingerprintParam = (params: URLSearchParams, key: string): string | undefined => {
    const value = params.get(key);
    return value !== null && FINGERPRINT.test(value) ? value.toLowerCase() : undefined;
};

/**
 * Reads the report filters from the URL so a view can be reloaded and shared. Unknown or malformed
 * values fall back to the defaults instead of breaking the page. The exposure token is deliberately
 * not part of the URL: it is requester-bound and lives in component state.
 */
export const parseAlphaAtlasSearchParams = (params: URLSearchParams): AlphaAtlasFilterState => {
    const entityKeys = entityKeyListParam(params, 'entity');
    const hide = params.get('hide');

    return {
        view: enumParam<AlphaAtlasView>(params, 'view', VIEWS) ?? DEFAULT_ALPHA_ATLAS_FILTERS.view,
        durationId: enumParam<AlphaAtlasDuration>(params, 'duration', DURATIONS) ?? DEFAULT_ALPHA_ATLAS_FILTERS.durationId,
        outcomeKind: enumParam<AlphaAtlasOutcomeKind>(params, 'outcome', OUTCOMES) ?? DEFAULT_ALPHA_ATLAS_FILTERS.outcomeKind,
        scope:
            entityKeys.length > 0 ? 'entity' : (enumParam<AlphaAtlasScope>(params, 'scope', SCOPES) ?? DEFAULT_ALPHA_ATLAS_FILTERS.scope),
        entityKeys,
        conditionKind: enumParam<AlphaAtlasConditionKind>(params, 'kind', CONDITION_KINDS),
        hypothesisFingerprint: fingerprintParam(params, 'hypothesis'),
        parentFingerprint: fingerprintParam(params, 'trigger'),
        childFingerprint: fingerprintParam(params, 'context'),
        direction: enumParam<AlphaAtlasDirection>(params, 'direction', DIRECTIONS),
        horizonSeconds: positiveIntParam(params, 'horizon'),
        windowRole: enumParam<AlphaAtlasWindowRole>(params, 'window', WINDOW_ROLES) ?? DEFAULT_ALPHA_ATLAS_FILTERS.windowRole,
        evidenceState: enumParam<AlphaAtlasEvidenceState>(params, 'evidence', EVIDENCE_STATES),
        hideInsufficientSupport: hide === null ? DEFAULT_ALPHA_ATLAS_FILTERS.hideInsufficientSupport : hide !== '0',
        minSupport: positiveIntParam(params, 'min'),
        acrossWindows: enumParam<AlphaAtlasAcrossWindows>(params, 'across', ACROSS) ?? DEFAULT_ALPHA_ATLAS_FILTERS.acrossWindows,
        bundleEntities: params.get('bundle') === '1',
        sortBy: enumParam<AlphaAtlasSortBy>(params, 'sort', SORTS) ?? DEFAULT_ALPHA_ATLAS_FILTERS.sortBy,
        page: positiveIntParam(params, 'page') ?? 1,
        pageSize: ALPHA_ATLAS_PAGE_SIZE
    };
};

/** Serializes only the values that differ from the defaults, keeping shared links short. */
export const buildAlphaAtlasSearchParams = (filters: AlphaAtlasFilterState): URLSearchParams => {
    const params = new URLSearchParams();
    if (filters.view !== DEFAULT_ALPHA_ATLAS_FILTERS.view) params.set('view', filters.view);
    if (filters.durationId !== DEFAULT_ALPHA_ATLAS_FILTERS.durationId) params.set('duration', filters.durationId);
    if (filters.outcomeKind !== DEFAULT_ALPHA_ATLAS_FILTERS.outcomeKind) params.set('outcome', filters.outcomeKind);
    if (filters.windowRole !== DEFAULT_ALPHA_ATLAS_FILTERS.windowRole) params.set('window', filters.windowRole);
    if (filters.scope === 'entity' && filters.entityKeys.length > 0) params.set('entity', filters.entityKeys.join(','));
    else if (filters.scope !== DEFAULT_ALPHA_ATLAS_FILTERS.scope) params.set('scope', filters.scope);
    if (filters.conditionKind) params.set('kind', filters.conditionKind);
    if (filters.hypothesisFingerprint) params.set('hypothesis', filters.hypothesisFingerprint);
    if (filters.parentFingerprint) params.set('trigger', filters.parentFingerprint);
    if (filters.childFingerprint) params.set('context', filters.childFingerprint);
    if (filters.direction) params.set('direction', filters.direction);
    if (filters.horizonSeconds !== undefined) params.set('horizon', String(filters.horizonSeconds));
    if (filters.evidenceState) params.set('evidence', filters.evidenceState);
    if (filters.hideInsufficientSupport !== DEFAULT_ALPHA_ATLAS_FILTERS.hideInsufficientSupport)
        params.set('hide', filters.hideInsufficientSupport ? '1' : '0');
    if (filters.minSupport !== undefined && filters.minSupport > 0) params.set('min', String(filters.minSupport));
    if (filters.acrossWindows !== DEFAULT_ALPHA_ATLAS_FILTERS.acrossWindows) params.set('across', filters.acrossWindows);
    if (filters.bundleEntities) params.set('bundle', '1');
    if (filters.sortBy !== DEFAULT_ALPHA_ATLAS_FILTERS.sortBy) params.set('sort', filters.sortBy);
    if (filters.page > 1) params.set('page', String(filters.page));
    return params;
};

/** Secondary filters that narrow the result set beyond the primary axes (slice, window, scope, direction, horizon). */
export const countActiveNarrowingFilters = (filters: AlphaAtlasFilterState): number =>
    [
        filters.conditionKind,
        filters.hypothesisFingerprint,
        filters.parentFingerprint,
        filters.childFingerprint,
        filters.evidenceState
    ].filter((value) => value !== undefined).length +
    (filters.scope === 'entity' && filters.entityKeys.length > 0 ? 1 : 0) +
    (filters.minSupport !== undefined && filters.minSupport > 0 ? 1 : 0) +
    (filters.acrossWindows !== 'any' ? 1 : 0);

/* ---------- Row interpretation ---------- */

export const ENTITY_CONCENTRATION_WARNING_SHARE = 1 / 3;
export const DATE_CONCENTRATION_WARNING_SHARE = 0.25;

/**
 * Plain-language reasons a result should be read with caution because a single entity or date supplies
 * a dominant share of its observations. Entity share is ignored for single-entity rows, where it is
 * always 100% by construction.
 */
export const describeConcentration = (row: {
    scope: AlphaAtlasScope;
    maximumEntityShare: number | null;
    maximumDateShare: number | null;
}): string[] => {
    const reasons: string[] = [];
    if (row.scope === 'pooled' && row.maximumEntityShare !== null && row.maximumEntityShare > ENTITY_CONCENTRATION_WARNING_SHARE)
        reasons.push(`One entity supplies ${(row.maximumEntityShare * 100).toFixed(0)}% of observations`);
    if (row.maximumDateShare !== null && row.maximumDateShare > DATE_CONCENTRATION_WARNING_SHARE)
        reasons.push(`One date supplies ${(row.maximumDateShare * 100).toFixed(0)}% of observations`);
    return reasons;
};

/** The selection-validation twin of a row in the shape `describeWindowConsistency` expects; undefined when that window has no row. */
export const validationTwinOf = (row: {
    validationRandomEdgeMeanMicros: number | null;
    validationEvidenceState: AlphaAtlasEvidenceState | null;
}): { randomEdgeMeanMicros: number | null; evidenceState: AlphaAtlasEvidenceState } | undefined =>
    row.validationEvidenceState === null
        ? undefined
        : { randomEdgeMeanMicros: row.validationRandomEdgeMeanMicros, evidenceState: row.validationEvidenceState };

/* ---------- Combination lookup ---------- */

/**
 * The lookup always needs one direction; the report's "both directions" choice has no meaning for a
 * single combination, so it falls back to long.
 */
export const buildAlphaAtlasCombinationQuery = (filters: AlphaAtlasFilterState): AlphaAtlasCombinationInput => ({
    durationId: filters.durationId,
    outcomeKind: filters.outcomeKind,
    parentFingerprint: filters.parentFingerprint,
    childFingerprint: filters.childFingerprint,
    scope: filters.scope === 'entity' && filters.entityKeys.length > 0 ? 'entity' : 'pooled',
    ...(filters.scope === 'entity' && filters.entityKeys[0] ? parseAlphaAtlasEntityKey(filters.entityKeys[0]) : {}),
    direction: filters.direction ?? 'long'
});

export type AlphaAtlasWindowConsistency = 'holds' | 'fades' | 'emerges' | 'negative' | 'sparse' | 'unknown';

export const WINDOW_CONSISTENCY_META: Record<
    AlphaAtlasWindowConsistency,
    { label: string; description: string; tone: 'neutral' | 'info' | 'success' | 'danger' | 'warning' }
> = {
    holds: {
        label: 'Holds up',
        description: 'Edge vs random entry is positive in both the historical and the selection-validation window.',
        tone: 'success'
    },
    fades: {
        label: 'Fades',
        description: 'Positive edge vs random in the historical window that is not positive in selection validation.',
        tone: 'warning'
    },
    emerges: {
        label: 'Emerges',
        description: 'No positive historical edge vs random, but positive in selection validation; recent behavior only.',
        tone: 'info'
    },
    negative: {
        label: 'Negative in both',
        description: 'Edge vs random entry is not positive in either controlled window.',
        tone: 'danger'
    },
    sparse: {
        label: 'Too sparse to judge',
        description: 'One of the windows has insufficient support, so the comparison is not meaningful.',
        tone: 'neutral'
    },
    unknown: { label: 'No comparison', description: 'One of the windows has no result or no random-entry baseline.', tone: 'neutral' }
};

/**
 * Compares the same hypothesis across the two controlled windows. Both windows are safe to read, so
 * this is the strongest robustness signal available without revealing verification evidence.
 */
export const describeWindowConsistency = (
    historical: { randomEdgeMeanMicros: number | null; evidenceState: AlphaAtlasEvidenceState } | undefined,
    validation: { randomEdgeMeanMicros: number | null; evidenceState: AlphaAtlasEvidenceState } | undefined
): AlphaAtlasWindowConsistency => {
    if (!historical || !validation || historical.randomEdgeMeanMicros === null || validation.randomEdgeMeanMicros === null)
        return 'unknown';
    if (historical.evidenceState === 'insufficientSupport' || validation.evidenceState === 'insufficientSupport') return 'sparse';
    const historicalPositive = historical.randomEdgeMeanMicros > 0;
    const validationPositive = validation.randomEdgeMeanMicros > 0;
    if (historicalPositive && validationPositive) return 'holds';
    if (historicalPositive) return 'fades';
    if (validationPositive) return 'emerges';
    return 'negative';
};

/* ---------- Entity breakdown ---------- */

export interface AlphaAtlasEntityBreakdownSummary {
    /** Entities that contributed at least one observation to the pooled result */
    entitiesWithObservations: number;
    /** Of those, how many have a positive edge vs random entry on their own */
    positiveEdgeEntities: number;
    /** Of those, how many clear the support rules on their own */
    sufficientSupportEntities: number;
    topEntityLabel: string | null;
    /** Share of all observations supplied by the single largest contributor */
    topEntitySupportShare: number | null;
}

/** Breadth of a pooled result: is the edge spread across the universe or carried by one or two names? */
export const summarizeEntityBreakdown = (
    rows: { entityLabel: string; supportCount: number; randomEdgeMeanMicros: number | null; evidenceState: AlphaAtlasEvidenceState }[]
): AlphaAtlasEntityBreakdownSummary => {
    let entitiesWithObservations = 0;
    let positiveEdgeEntities = 0;
    let sufficientSupportEntities = 0;
    let totalSupport = 0;
    let top: { entityLabel: string; supportCount: number } | null = null;
    for (const row of rows) {
        if (row.supportCount <= 0) continue;
        entitiesWithObservations += 1;
        totalSupport += row.supportCount;
        if (row.randomEdgeMeanMicros !== null && row.randomEdgeMeanMicros > 0) positiveEdgeEntities += 1;
        if (row.evidenceState !== 'insufficientSupport') sufficientSupportEntities += 1;
        if (!top || row.supportCount > top.supportCount) top = { entityLabel: row.entityLabel, supportCount: row.supportCount };
    }
    return {
        entitiesWithObservations,
        positiveEdgeEntities,
        sufficientSupportEntities,
        topEntityLabel: top?.entityLabel ?? null,
        topEntitySupportShare: top && totalSupport > 0 ? top.supportCount / totalSupport : null
    };
};

/* ---------- Evidence badge ---------- */

/**
 * Published rows show their campaign evidence state. Bundle rows are ad-hoc aggregates that no campaign
 * ever evaluated, so they only say whether the pooled support rules pass.
 */
export const describeEvidenceBadge = (row: {
    aggregation: 'published' | 'bundle';
    evidenceState: AlphaAtlasEvidenceState;
}): { label: string; description: string; tone: 'neutral' | 'info' | 'success' | 'danger' | 'warning' | 'accent' } => {
    if (row.aggregation === 'bundle') {
        return row.evidenceState === 'insufficientSupport'
            ? {
                  label: 'Bundle · sparse',
                  tone: 'neutral',
                  description:
                      'Ad-hoc aggregate over the selected entities that falls below the pooled support rules (30 observations, 5 dates, 3 entities). Read with caution.'
              }
            : {
                  label: 'Bundle · enough data',
                  tone: 'info',
                  description:
                      'Ad-hoc aggregate over the selected entities that clears the pooled support rules. It was never part of a validation campaign, so it has no selection or verification status.'
              };
    }
    const meta = EVIDENCE_STATE_META[row.evidenceState];
    return { label: meta.label, description: meta.description, tone: meta.tone };
};
